import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CryptoDepositStatus,
  CryptoWithdrawStatus,
  FiatDepositStatus,
  FiatWithdrawStatus,
  walletHistoryTabType,
  SpecificWalletHistoryTabType
} from './types';

// Generic filter type with a status that can be of any type
export type WalletHistoryFilter<StatusType = string> = {
  asset?: string;
  status?: StatusType;
  createdTimeStart?: string;
  createdTimeEnd?: string;
};

// Define specific filter types for each tab
export type CryptoDepositFilter = WalletHistoryFilter<CryptoDepositStatus>;
export type CryptoWithdrawFilter = WalletHistoryFilter<CryptoWithdrawStatus>;
export type FiatDepositFilter = WalletHistoryFilter<FiatDepositStatus>;
export type FiatWithdrawFilter = WalletHistoryFilter<FiatWithdrawStatus>;
export type DistributionsFilter = WalletHistoryFilter<string>;

// Define the state type with specific status types for each tab
type HistoryStateType = {
  walletHistory: {
    cryptoDeposit: CryptoDepositFilter;
    cryptoWithdraw: CryptoWithdrawFilter;
    fiatDeposit: FiatDepositFilter;
    fiatWithdraw: FiatWithdrawFilter;
    distributions: DistributionsFilter;
  };
};

// Type map to ensure type safety when setting status
export type StatusTypeMap = {
  cryptoDeposit: CryptoDepositStatus;
  cryptoWithdraw: CryptoWithdrawStatus;
  fiatDeposit: FiatDepositStatus;
  fiatWithdraw: FiatWithdrawStatus;
  distributions: string;
};

// Type map for wallet history filters
export type WalletHistoryFilterMap = {
  cryptoDeposit: CryptoDepositFilter;
  cryptoWithdraw: CryptoWithdrawFilter;
  fiatDeposit: FiatDepositFilter;
  fiatWithdraw: FiatWithdrawFilter;
  distributions: DistributionsFilter;
};

const initialState: HistoryStateType = {
  walletHistory: {
    cryptoDeposit: {
      asset: '',
      status: undefined,
      createdTimeStart: '',
      createdTimeEnd: ''
    },
    cryptoWithdraw: {
      asset: '',
      status: undefined,
      createdTimeStart: '',
      createdTimeEnd: ''
    },
    fiatDeposit: {
      status: undefined,
      createdTimeStart: '',
      createdTimeEnd: ''
    },
    fiatWithdraw: {
      status: undefined,
      createdTimeStart: '',
      createdTimeEnd: ''
    },
    distributions: {
      createdTimeStart: '',
      createdTimeEnd: ''
    }
  }
};

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    setWalletAssetFilter: (
      state,
      action: PayloadAction<{
        tab: SpecificWalletHistoryTabType;
        asset: string;
      }>
    ) => {
      const { tab, asset } = action.payload;
      if (
        state.walletHistory[tab] &&
        tab !== 'fiatDeposit' &&
        tab !== 'fiatWithdraw'
      ) {
        state.walletHistory[tab].asset = asset;
      }
    },
    setDateFilters: (
      state,
      action: PayloadAction<{
        tab: SpecificWalletHistoryTabType;
        createdTimeStart: string;
        createdTimeEnd: string;
      }>
    ) => {
      const { tab, createdTimeStart, createdTimeEnd } = action.payload;
      if (state.walletHistory[tab]) {
        if (createdTimeStart !== undefined)
          state.walletHistory[tab].createdTimeStart = createdTimeStart;
        if (createdTimeEnd !== undefined)
          state.walletHistory[tab].createdTimeEnd = createdTimeEnd;
      }
    },
    // Using a more type-safe approach with action creators
    setStatusFilter: {
      reducer: (
        state,
        action: PayloadAction<{
          tab: SpecificWalletHistoryTabType;
          status: any;
        }>
      ) => {
        const { tab, status } = action.payload;
        if (state.walletHistory[tab]) {
          // Handle each tab type specifically to satisfy TypeScript
          switch (tab) {
            case 'cryptoDeposit':
              (state.walletHistory.cryptoDeposit.status as any) = status;
              break;
            case 'cryptoWithdraw':
              (state.walletHistory.cryptoWithdraw.status as any) = status;
              break;
            case 'fiatDeposit':
              (state.walletHistory.fiatDeposit.status as any) = status;
              break;
            case 'fiatWithdraw':
              (state.walletHistory.fiatWithdraw.status as any) = status;
              break;
            case 'distributions':
              state.walletHistory.distributions.status = status;
              break;
            default:
              break;
          }
        }
      },
      prepare: <T extends SpecificWalletHistoryTabType>({
        tab,
        status
      }: {
        tab: T;
        status: StatusTypeMap[T];
      }) => {
        return { payload: { tab, status } };
      }
    },
    setAllFilters: {
      reducer: (
        state,
        action: PayloadAction<{
          tab: SpecificWalletHistoryTabType;
          filters: any;
        }>
      ) => {
        const { tab, filters } = action.payload;
        // Handle each tab type specifically to avoid TypeScript errors
        switch (tab) {
          case 'cryptoDeposit':
            state.walletHistory.cryptoDeposit = {
              ...state.walletHistory.cryptoDeposit,
              ...filters
            };
            break;
          case 'cryptoWithdraw':
            state.walletHistory.cryptoWithdraw = {
              ...state.walletHistory.cryptoWithdraw,
              ...filters
            };
            break;
          case 'fiatDeposit':
            state.walletHistory.fiatDeposit = {
              ...state.walletHistory.fiatDeposit,
              ...filters
            };
            break;
          case 'fiatWithdraw':
            state.walletHistory.fiatWithdraw = {
              ...state.walletHistory.fiatWithdraw,
              ...filters
            };
            break;
          case 'distributions':
            state.walletHistory.distributions = {
              ...state.walletHistory.distributions,
              ...filters
            };
            break;
          default:
            break;
        }
      },
      prepare: <T extends SpecificWalletHistoryTabType>({
        tab,
        filters
      }: {
        tab: T;
        filters: Partial<WalletHistoryFilterMap[T]>;
      }) => {
        return { payload: { tab, filters } };
      }
    },
    clearWalletHistoryFilters: (
      state,
      action: PayloadAction<SpecificWalletHistoryTabType>
    ) => {
      const tab = action.payload;
      // Handle each tab type specifically to avoid TypeScript errors
      switch (tab) {
        case 'cryptoDeposit':
          state.walletHistory.cryptoDeposit =
            initialState.walletHistory.cryptoDeposit;
          break;
        case 'cryptoWithdraw':
          state.walletHistory.cryptoWithdraw =
            initialState.walletHistory.cryptoWithdraw;
          break;
        case 'fiatDeposit':
          state.walletHistory.fiatDeposit =
            initialState.walletHistory.fiatDeposit;
          break;
        case 'fiatWithdraw':
          state.walletHistory.fiatWithdraw =
            initialState.walletHistory.fiatWithdraw;
          break;
        case 'distributions':
          state.walletHistory.distributions =
            initialState.walletHistory.distributions;
          break;
        default:
          break;
      }
    },
    clearAllFilters: (state) => {
      state.walletHistory = initialState.walletHistory;
    }
  }
});

export const {
  setWalletAssetFilter,
  setDateFilters,
  setStatusFilter,
  setAllFilters,
  clearWalletHistoryFilters,
  clearAllFilters
} = historySlice.actions;

export default historySlice.reducer;
